import styled from 'styled-components';
import { graphql, Link, useStaticQuery } from 'gatsby';
import React, { FC, memo, useState } from 'react';
import { IconHamburger } from '@lmn/lastminute-react-icons/lastminute-icons-linear';
import lastminuteLogo from '../../../../content/assets/lastminute-logo.svg';
import { Heading3 } from '../atoms/heading';
import { mediaQuery } from '../utils-css/media-query';
import { ScrollDirection, useScrollDirection } from '../hooks/use-scroll-direction';
import { Overlay } from '../atoms/overlay';
import { Menu } from '../molecules/menu';
import { Search } from './search';
import { SearchResult } from '../../../types/search';
import { SearchHits } from './search-hits';

interface ShouldOpenMenuProp {
  $openMenu: boolean;
}

type HeaderSectionProps = ShouldOpenMenuProp & {
  $shouldHide: boolean;
};

const menuHeight = '50px';

const HeaderSection = styled.header<HeaderSectionProps>`
  position: fixed;
  transition:
    top 0.3s ease 0s,
    height 0.3s ease 0s;
  height: ${(props): string => (props.$openMenu ? '70%' : menuHeight)};
  top: ${(props): string => (props.$shouldHide ? `-${menuHeight}` : '0')};
  overflow: hidden;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  z-index: 1000;
  ${({ theme }): string => theme.mixins.padding({ hor: 2, ver: 0 })};
  ${({ theme }): string => theme.mixins.margin({ top: 0, right: 'auto', bottom: 7, left: 'auto' })};
  ${({ theme }): string => theme.mixins.elevation('small')};

  ${mediaQuery.minWidth.lg} {
    flex-direction: row;
    overflow: visible;
  }
`;

interface LogoAndTitleContainerProps {
  $shouldHide: boolean;
}

const LogoAndTitleContainer = styled.section<LogoAndTitleContainerProps>`
  display: flex;
  align-items: center;
  opacity: ${(props): string => (props.$shouldHide ? '0' : '1')};
  transition: opacity 0.2s ease ${(props): string => (props.$shouldHide ? '0' : '0.3s')};

  ${mediaQuery.minWidth.sm} {
    opacity: 1;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  text-decoration: none;
`;

const LogoImage = styled.img`
  height: ${menuHeight};
  width: 210px;
  margin: 0;
`;

const LogoTitle = styled(Heading3)`
  display: none;
  ${({ theme }): string => theme.mixins.margin({ hor: 2, ver: 0 })};

  ${mediaQuery.minWidth.lg} {
    display: flex;
  }
`;

const Divider = styled.div`
  display: none;

  ${mediaQuery.minWidth.lg} {
    height: 30px;
    width: 1px;
    ${({ theme }): string => theme.mixins.margin({ right: 2 })};
    background-color: ${({ theme }): string => theme.commons.colors.primary};
    display: block;
  }
`;

const Hamburger = styled(IconHamburger)`
  position: absolute;
  top: 10px;
  left: 10px;
  color: ${({ theme }): string => theme.commons.colors.primary};
  height: 30px;
  width: 30px;

  ${mediaQuery.minWidth.lg} {
    display: none;
  }
`;

interface LogoProps {
  startSearch: boolean;
  title: string;
}

const Logo = memo<LogoProps>(({ startSearch, title }) => (
  <LogoLink to="/">
    <LogoAndTitleContainer $shouldHide={startSearch}>
      <LogoImage src={lastminuteLogo} alt="lastminute.com logo" />
      <LogoTitle>{title}</LogoTitle>
    </LogoAndTitleContainer>
  </LogoLink>
));

export const Header: FC<{ pathname?: string }> = ({ pathname }) => {
  const data = useStaticQuery<Queries.HeaderQuery>(graphql`
    query Header {
      site {
        siteMetadata {
          title
          careers
          meetup
          planYourJourney
          companyUrl
        }
      }
    }
  `);

  const direction = useScrollDirection();
  const [openMenu, setOpenMenu] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [searching, setSearching] = useState(false);
  const [hits, setHits] = useState<SearchResult[]>([]);

  return (
    <>
      <HeaderSection $shouldHide={direction === ScrollDirection.down} $openMenu={openMenu}>
        <Hamburger
          onClick={(): void => {
            if (!searching) {
              setOpenMenu(!openMenu);
            }

            if (!openMenu) {
              setShowOverlay(!showOverlay);
            }
          }}
        />
        <Logo startSearch={searching} title={data.site!.siteMetadata.title} />
        <Menu $openMenu={openMenu} data={data.site!} pathname={pathname} />
        <Divider />
        <Search
          $openMenu={openMenu}
          onClick={() => {
            setSearching(!searching);
            setShowOverlay(!showOverlay);
          }}
          searching={searching}
          onHits={(newHits) => setHits(newHits)}
        />
      </HeaderSection>
      {showOverlay && (
        <Overlay
          $openMenu={openMenu || searching}
          $delay="0.2s"
          onClick={(): void => {
            if (openMenu) {
              setOpenMenu(false);
            }
            if (searching) {
              setSearching(false);
            }
          }}
          onTransitionEnd={() => setShowOverlay(!showOverlay)}
        >
          {hits.length > 0 && <SearchHits hits={hits} />}
        </Overlay>
      )}
    </>
  );
};
